import { useState, useEffect, useContext, useCallback } from "react";
import { UserContext } from "../context/UserContext";
import api from "../api/axios";

const ID_TYPE_LABELS = {
  passport: "Passport",
  drivers_license: "Driver's License",
  national_id: "National ID",
  voters_card: "Voter's Card",
};

export default function KYC() {
  const { setUser } = useContext(UserContext);

  const [form, setForm] = useState({ fullName: "", dob: "", idType: "", idNumber: "", address: "" });
  const [kycInfo, setKycInfo] = useState({});
  const [accountFullName, setAccountFullName] = useState("");
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [status, setStatus] = useState("");
  const [loadingKyc, setLoadingKyc] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const STATUS_META = {
    pending: { label: "Awaiting approval", className: "bg-amber-500/15 border border-amber-500/30 text-amber-300" },
    verified: { label: "Approved", className: "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300" },
    rejected: { label: "Rejected", className: "bg-red-500/15 border border-red-500/30 text-red-300" },
  };

  const applyKycData = useCallback((data) => {
    const info = {
      ...(data?.kycInfo || {}),
      fullName: data?.kycInfo?.fullName || data?.fullName || "",
    };
    const normalizedStatus = (data?.kycStatus || "").toLowerCase();

    setAccountFullName(data?.fullName || "");
    setKycInfo(info);
    setForm({
      fullName: info.fullName,
      dob: info.dob || "",
      idType: info.idType || "",
      idNumber: info.idNumber || "",
      address: info.address || "",
    });
    setStatus(normalizedStatus);
    setUser({ kycStatus: normalizedStatus });
  }, [setUser]);

  const refreshKyc = useCallback(async () => {
    const { data } = await api.get("/kyc");
    applyKycData(data);
  }, [applyKycData]);

  useEffect(() => {
    let active = true;
    setError("");
    api.get("/kyc")
      .then(({ data }) => {
        if (active) applyKycData(data);
      })
      .catch((err) => {
        console.error(err);
        if (active) setError("Failed to load KYC data.");
      })
      .finally(() => {
        if (active) setLoadingKyc(false);
      });

    return () => { active = false; };
  }, [applyKycData]);

  useEffect(() => {
    if (status !== "pending") return undefined;
    const intervalId = window.setInterval(() => {
      refreshKyc().catch((err) => console.error("Failed to refresh KYC status:", err));
    }, 15000);

    return () => window.clearInterval(intervalId);
  }, [status, refreshKyc]);


  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleFileChange = (e, type) => type === "id" ? setIdFile(e.target.files[0]) : setSelfieFile(e.target.files[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setError("");

    if (!idFile || !selfieFile) {
      setError("Please upload both ID document and selfie.");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    Object.keys(form).forEach(key => formData.append(key, form[key]));
    formData.append("idFile", idFile);
    formData.append("selfie", selfieFile);

    try {
      const { data } = await api.post("/kyc", formData);
      applyKycData(data);
      setMessage("KYC submitted successfully!");
    } catch (err) {
      console.error(err);
      const details = err.response?.data?.details;
      setError(details?.length ? details.join(" · ") : err.response?.data?.message || "Server error. Try again later.");
    }
    finally { setLoading(false); }
  };

  const statusMeta = STATUS_META[status] || {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : "Not submitted",
    className: "bg-slate-700/40 border border-slate-600 text-slate-300",
  };

  if (loadingKyc) {
    return <p className="mt-10 text-center text-slate-300">Loading KYC details...</p>;
  }

  if (status === "pending" || status === "verified") {
    const isApproved = status === "verified";

    return (
      <div className="mx-auto max-w-3xl space-y-6 text-white">
        <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">KYC Verification</h1>
              <p className="mt-1 text-sm text-slate-300">
                {isApproved
                  ? "Your identity verification is approved. Your submitted details are shown below."
                  : "Your submitted details are awaiting approval. We’ll update this page when the status changes."}
              </p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusMeta.className}`}>
              {statusMeta.label}
            </span>
          </div>
        </section>

        <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
          <h2 className="text-lg font-semibold">Submitted details</h2>
          <dl className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-400">Full name</dt>
              <dd className="mt-1 text-sm text-white">{kycInfo.fullName || accountFullName || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-400">Date of birth</dt>
              <dd className="mt-1 text-sm text-white">{kycInfo.dob || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-400">ID type</dt>
              <dd className="mt-1 text-sm text-white">{ID_TYPE_LABELS[kycInfo.idType] || kycInfo.idType || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-400">ID number</dt>
              <dd className="mt-1 text-sm text-white">{kycInfo.idNumber || "—"}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-wider text-slate-400">Residential address</dt>
              <dd className="mt-1 whitespace-pre-wrap text-sm text-white">{kycInfo.address || "—"}</dd>
            </div>
          </dl>

        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 text-white">
      <div className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white">KYC Verification</h2>
            <p className="mt-1 text-sm text-slate-300">
              {status === "rejected" ? "Your submission was rejected. Update your details and submit again." : "Complete KYC to access all account features."}
            </p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusMeta.className}`}>
            {statusMeta.label}
          </span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="text" name="fullName" placeholder="Full Name" className="w-full rounded-xl border border-white/15 bg-white/5 p-3 text-white placeholder:text-slate-400" value={form.fullName} onChange={handleChange} required />
          <input type="date" name="dob" className="w-full rounded-xl border border-white/15 bg-white/5 p-3 text-white" value={form.dob} onChange={handleChange} required />
          <select name="idType" className="w-full rounded-xl border border-white/15 bg-white/5 p-3 text-white" value={form.idType} onChange={handleChange} required>
            <option value="">Select ID Type</option>
            <option value="passport">Passport</option>
            <option value="drivers_license">Driver's License</option>
            <option value="national_id">National ID</option>
            <option value="voters_card">Voter's Card</option>
          </select>
          <input type="text" name="idNumber" placeholder="ID Number" className="w-full rounded-xl border border-white/15 bg-white/5 p-3 text-white placeholder:text-slate-400" value={form.idNumber} onChange={handleChange} required />
          <textarea name="address" placeholder="Residential Address" className="w-full rounded-xl border border-white/15 bg-white/5 p-3 text-white placeholder:text-slate-400" value={form.address} onChange={handleChange} required />

          <div className="rounded-xl border border-white/10 bg-white/5 p-3">
            <label className="mb-2 block text-sm text-slate-300">Upload ID Document</label>
            <input type="file" onChange={(e) => handleFileChange(e, "id")} required className="text-sm text-slate-300 file:mr-3 file:rounded-lg file:border-0 file:bg-cyan-300/20 file:px-3 file:py-2 file:text-cyan-300" />
            {idFile && <p className="mt-2 text-xs text-slate-400">{idFile.name}</p>}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-3">
            <label className="mb-2 block text-sm text-slate-300">Upload Selfie</label>
            <input type="file" onChange={(e) => handleFileChange(e, "selfie")} required className="text-sm text-slate-300 file:mr-3 file:rounded-lg file:border-0 file:bg-cyan-300/20 file:px-3 file:py-2 file:text-cyan-300" />
            {selfieFile && <p className="mt-2 text-xs text-slate-400">{selfieFile.name}</p>}
          </div>

          <button type="submit" disabled={loading} className="w-full rounded-xl bg-accent py-3 font-semibold text-slate-950 transition hover:opacity-90 disabled:opacity-50">
            {loading ? "Submitting..." : "Submit for Verification"}
          </button>
          {message && <p className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">{message}</p>}
          {error && <p className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</p>}
        </form>
      </div>
    </div>
  );
}
